<script setup lang="ts">
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from 'element-plus'
import { ref } from 'vue'

import { BoxComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '..'
import CheckIcon from '../../assets/icons/check.svg?component'
import type { SelectorOption } from '../types/ui'

const props = withDefaults(
  defineProps<{
    options: SelectorOption[]
    placeholder?: string
    loading?: boolean
    emptyText?: string
    width?: string | number
    /** Ids the host already considers added, so a re-shown option can render as such from the start. */
    addedIds?: number[]
    /** Set when the host already explains the empty options via the `status` slot, so the generic text would be redundant. */
    hideEmptyState?: boolean
  }>(),
  {
    placeholder: 'Search...',
    emptyText: 'No matching data',
    loading: false,
    addedIds: () => [],
    hideEmptyState: false,
  },
)

const isAdded = (id: number) => props.addedIds.includes(id)

const emits = defineEmits<{
  search: [value: string]
  bulkSearch: [tokens: string[]]
  /** The whole row is the click target, so the slot content stays a passive rendering of the option. */
  add: [item: SelectorOption]
  'visible-change': [visible: boolean]
}>()

defineSlots<{
  option?: (props: { item: SelectorOption; added: boolean }) => unknown
  /** Rendered at the top of the open panel, above the suggestions/empty state — for host-owned status messages (e.g. a failed bulk lookup) that must stay visible while the panel floats over the rest of the page. */
  status?: () => unknown
}>()

const query = defineModel<string>('query', { default: '' })

const dropdownRef = ref<InstanceType<typeof ElDropdown>>()

const openMenu = (event?: Event) => dropdownRef.value?.popperRef?.onOpen(event)
const closeMenu = () => dropdownRef.value?.handleClose()

/** Element Plus defaults the popper 12px off the input; the design sits the list right under it. */
const MENU_OFFSET = 4

const popperOptions = {
  modifiers: [
    // Appended after Element Plus's own modifiers, so this overrides its default offset.
    {
      name: 'offset',
      options: { offset: [0, MENU_OFFSET] },
    },
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite' as const,
      requires: ['computeStyles'] as string[],
      fn: ({ state }: { state: { elements: { popper: HTMLElement }; rects: { reference: { width: number } } } }) => {
        state.elements.popper.style.minWidth = `${state.rects.reference.width}px`
      },
    },
  ],
}

const handleInput = (value: string) => {
  const tokens = value.split(/[\s,;]+/).filter(Boolean)
  if (tokens.length > 1) {
    emits('bulkSearch', tokens)
  } else {
    emits('search', value)
  }
}
</script>

<template>
  <ElDropdown
    ref="dropdownRef"
    :trigger="[]"
    :trigger-keys="[]"
    :hide-on-click="false"
    :show-arrow="false"
    placement="bottom-start"
    :popper-options="popperOptions"
    @visible-change="emits('visible-change', $event)"
  >
    <InputComponent
      v-model="query"
      :placeholder="placeholder"
      @input="handleInput"
      @click="openMenu"
      @focusin="openMenu"
      @keydown.down.prevent="openMenu"
      @keydown.esc="closeMenu"
    />

    <template #dropdown>
      <BoxComponent :w="width" class="multi-add-selector__menu">
        <slot name="status" />
        <BoxComponent v-if="loading" :p="16">
          <LoadingComponent />
        </BoxComponent>
        <template v-else>
          <ElDropdownMenu v-if="options.length">
            <ElDropdownItem v-for="item in options" :key="item.id" @click="emits('add', item)">
              <slot name="option" :item="item" :added="isAdded(item.id)">
                <BoxComponent :p="16" w="100%">
                  <FlexComponent justify="space-between" align="center" :gap="12">
                    <TypographyComponent variant="body-large">{{ item.label }}</TypographyComponent>
                    <ElIcon v-if="isAdded(item.id)" :size="24">
                      <CheckIcon />
                    </ElIcon>
                  </FlexComponent>
                </BoxComponent>
              </slot>
            </ElDropdownItem>
          </ElDropdownMenu>
          <BoxComponent v-else-if="!hideEmptyState" :pt="16" :pb="16">
            <TypographyComponent is="p" variant="body-small" text-align="center">{{ emptyText }}</TypographyComponent>
          </BoxComponent>
        </template>
      </BoxComponent>
    </template>
  </ElDropdown>
</template>

<style lang="scss" scoped>
.el-dropdown {
  display: flex;
  width: 100%;

  .el-tooltip__trigger {
    flex: 1;
    min-width: 0;
  }
}
.multi-add-selector__menu {
  border-radius: 16px;
  overflow: hidden;
}
:deep(.el-dropdown-menu) {
  padding: 0;
}
:deep(.el-dropdown-menu__item) {
  padding: 0;
  white-space: normal;
}
</style>
